import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch, connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { useTranslation } from '../../../../../src/i18n';
import {
  TP,
  MEDIA_TYPES_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  MEDIA_TAGS_TYPES_IDS,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  TAGS_AVAILABILITY_TYPES,
} from '../../../../../constants/consts';
import EditMediaCard from '../../../../../components/EditComponents/EditMediaCard';
import AddVideoModal from '../AddVideoModal';
import AddMediaDetailsModal from '../../../../../components/EditComponents/AddMediaDetailsModal';
import Button from '../../../../../components/Button';
import { MEDIA, MEDIA_ATTRIBUTES } from '../../../../../constants/endpointConstants';
import { updateOne, createEntity, getWholeList, useBufferValueAction } from '../../../../App/actions';
import {
  updateVideoAction,
  addVideoAction,
  getArtistMediaAttrTypesAction,
  getVideoAction,
  deleteVideoAction,
  getArtistVideoFormatAttrTypesAction,
} from '../../../actions';
import {
  selectVideo,
  selectMediaAttrTypesOptions,
  selectUpdateMediaLoading,
  selectArtistVideoFormatsNames,
} from '../../../selectors';
import FbCircularProgress from '../../../../../components/FbCircularProgress';
import ActionStatusModal from '../../../../../components/ActionStatusModal';
import TextButton from '../../../../../components/TextButton';
import { selectBufferValueData } from '../../../../App/selectors';
import EditMediaLayout from '../../../../../components/EditComponents/EditMediaLayout';
import { getMediaTagsWithoutEntityTag } from '../../../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  cardsSection: {
    marginBottom: theme.spacing(4),
  },
}));

const VideosTab = ({
  title,
  subtitle,
  goToNextTab,
  profile,
  privacyOptions,
  videoAttributesOptions,
  updatingVideo,
  deleteVideoData,
  videoFormats,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const video = useSelector(selectVideo());

  const [isAddModalOpened, setAddModalState] = React.useState(false);
  const [isEditModalOpened, setEditModalState] = React.useState(false);
  const [editModalVideo, setEditModalVideo] = React.useState(null);
  const [videoToDelete, setVideoToDelete] = React.useState(null);

  React.useEffect(() => {
    dispatch(
      getWholeList({
        endpoint: MEDIA,
        sagaRoutine: getVideoAction,
        params: {
          queryParams: {
            media_type_id: MEDIA_TYPES_IDS.VIDEO,
            media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PROFILE,
            media_tag_id: profile?.id,
          },
        },
      }),
    );
    dispatch(
      getWholeList({
        endpoint: MEDIA_ATTRIBUTES,
        sagaRoutine: getArtistMediaAttrTypesAction,
        params: {
          queryParams: {
            attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO,
          },
        },
      }),
    );
    if (!videoFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getArtistVideoFormatAttrTypesAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO_FORMAT,
            },
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddVideoModal = () => {
    setAddModalState(true);
  };

  const closeAddVideoModal = () => {
    setAddModalState(false);
  };

  const openEditVideoModal = chosenVideo => {
    setEditModalState(true);
    setEditModalVideo(chosenVideo);
  };

  const closeEditVideoModal = () => {
    setEditModalState(false);
    setEditModalVideo(null);
  };

  const uploadVideo = data => {
    const videoParams = data.url ? { url: data.url } : { file: { data: `@${data?.file?.filename}` } };
    dispatch(
      createEntity({
        endpoint: MEDIA,
        sagaRoutine: addVideoAction,
        params: {
          ...videoParams,
          mediaType: {
            id: MEDIA_TYPES_IDS.VIDEO,
          },
          privacyAttribute: {
            id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
          },
          tags: [
            {
              tagType: { id: MEDIA_TAGS_TYPES_IDS.PROFILE },
              tagId: profile?.id,
              availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
            },
          ],
        },
        ...(data.file ? { files: { [data?.file?.filename]: data?.file?.file } } : {}),
        callback: closeAddVideoModal,
      }),
    );
  };

  const updateVideo = (updatedVideo, id, videoPreview) => {
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id,
        sagaRoutine: updateVideoAction,
        params: updatedVideo,
        callback: closeEditVideoModal,
        ...(videoPreview?.filename ? { files: { [videoPreview.filename]: videoPreview.file } } : null),
      }),
    );
  };

  const changePrivacy = (v, id) => {
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id,
        sagaRoutine: updateVideoAction,
        params: {
          privacyAttribute: { id: v?.value },
        },
      }),
    );
  };

  const deleteVideo = () => {
    const videoTags = getMediaTagsWithoutEntityTag(videoToDelete?.tags, MEDIA_TAGS_TYPES_IDS.PROFILE, profile?.id);
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id: videoToDelete?.id,
        sagaRoutine: useBufferValueAction,
        params: {
          tags: videoTags,
        },
        callback: updatedVideo => {
          dispatch(deleteVideoAction(updatedVideo?.id));
          closeConfirmationModal();
        },
      }),
    );
  };

  const closeConfirmationModal = () => {
    setVideoToDelete(null);
  };

  return (
    <>
      <EditMediaLayout
        title={title}
        subtitle={subtitle}
        addButtonClick={openAddVideoModal}
        buttonTitle={`${TP}.FN_ADD_VIDEO`}
        isEmpty={video?.data?.length === 0 && video?.loadedOnce}
        emptyMessage={`${TP}.m_ARTIST_NO_VIDEOS`}
      >
        <section className={classes.cardsSection}>
          {video?.loading || !video?.loadedOnce ? (
            <FbCircularProgress />
          ) : (
            (video?.data || []).map(videoRecord => (
              <EditMediaCard
                media={videoRecord}
                editClick={() => openEditVideoModal(videoRecord)}
                key={videoRecord?.id}
                privacyOptions={privacyOptions}
                mediaType="video"
                onPrivacyChange={changePrivacy}
                onDelete={() => setVideoToDelete(videoRecord)}
              />
            ))
          )}
        </section>
        <Button onClick={goToNextTab}>{t(`${TP}.m_NEXTSTEP`)}</Button>
      </EditMediaLayout>
      {isAddModalOpened && (
        <AddVideoModal
          isOpened={isAddModalOpened}
          onClose={closeAddVideoModal}
          uploadVideo={uploadVideo}
          isLoading={updatingVideo}
          formats={videoFormats}
        />
      )}
      {isEditModalOpened && (
        <AddMediaDetailsModal
          isOpened={isEditModalOpened}
          onClose={closeEditVideoModal}
          submitFunc={updateVideo}
          entity={editModalVideo}
          type="video"
          typeOptions={videoAttributesOptions}
          isLoading={updatingVideo}
        />
      )}
      {videoToDelete && (
        <ActionStatusModal
          isOpened
          onClose={closeConfirmationModal}
          type="warning"
          title={t(`${TP}.FN_DELETE_ARTIST_VIDEO_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={deleteVideo}>{t(`${TP}.m_DELETE`)}</TextButton>,
            <Button onClick={closeConfirmationModal}>{t(`${TP}.m_CANCEL`)}</Button>,
          ]}
        >
          {deleteVideoData?.loading && <FbCircularProgress />}
        </ActionStatusModal>
      )}
    </>
  );
};

VideosTab.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  goToNextTab: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  privacyOptions: PropTypes.array.isRequired,
  deleteVideoData: PropTypes.object,
  videoFormats: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  videoAttributesOptions: selectMediaAttrTypesOptions(),
  updatingVideo: selectUpdateMediaLoading(),
  deleteVideoData: selectBufferValueData(),
  videoFormats: selectArtistVideoFormatsNames(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(VideosTab);
